import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SimpleHeader from '../components/SimpleHeader';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader } from 'lucide-react';
import { getCheckoutStatus } from '../services/paymentService';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const data = await getCheckoutStatus(sessionId);
        setPaymentInfo(data);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
        } else {
          // Poll for status if not yet paid
          setTimeout(() => checkPaymentStatus(), 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#F5F1E8]">
      <SimpleHeader />
      
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-2xl mx-auto">
          {status === 'loading' && (
            <Card className="bg-white p-12 text-center">
              <Loader className="w-16 h-16 text-[#6B7A5C] mx-auto mb-6 animate-spin" />
              <h1 className="text-3xl font-bold text-[#2C1810] mb-4">
                Processing Your Payment...
              </h1>
              <p className="text-[#5A5046]">
                Please wait while we confirm your purchase.
              </p>
            </Card>
          )}

          {status === 'success' && (
            <Card className="bg-white p-12 text-center">
              <CheckCircle className="w-20 h-20 text-[#6B7A5C] mx-auto mb-6" />
              
              <h1 className="text-4xl font-bold text-[#2C1810] mb-4">
                Payment Successful!
              </h1>
              
              <p className="text-[#5A5046] text-lg mb-8">
                Thank you for your purchase! Your order has been confirmed.
              </p>
              
              {paymentInfo && (
                <div className="bg-[#EBE6D8] p-6 rounded-lg mb-8 text-left">
                  <h3 className="font-bold text-[#2C1810] mb-3">Order Details:</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#5A5046]">Product:</span>
                      <span className="font-semibold text-[#2C1810]">
                        {paymentInfo.metadata?.product_name}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#5A5046]">Amount:</span>
                      <span className="font-semibold text-[#2C1810]">
                        ${paymentInfo.amount_total?.toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#5A5046]">Order ID:</span>
                      <span className="font-mono text-xs text-[#2C1810]">
                        {sessionId?.substring(0, 20)}...
                      </span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-[#6B7A5C] bg-opacity-10 p-6 rounded-lg mb-8">
                <h3 className="font-bold text-[#2C1810] mb-2">
                  📧 Check Your Email
                </h3>
                <p className="text-[#5A5046] text-sm">
                  We've sent you a confirmation email with access to your purchased content.
                  If you don't see it, check your spam folder.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {paymentInfo?.metadata?.product_id === 'community' ? (
                  <>
                    <Link to="/community/portal">
                      <Button className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white px-8 py-3 rounded-lg" data-testid="enter-community-btn">
                        Enter the Community
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="border-2 border-[#6B7A5C] text-[#6B7A5C] hover:bg-[#6B7A5C] hover:text-white px-8 py-3 rounded-lg">
                        Back to Home
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/access">
                      <Button className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white px-8 py-3 rounded-lg" data-testid="access-guides-btn">
                        Access My Guides
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="border-2 border-[#6B7A5C] text-[#6B7A5C] hover:bg-[#6B7A5C] hover:text-white px-8 py-3 rounded-lg">
                        Back to Home
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </Card>
          )}

          {status === 'error' && (
            <Card className="bg-white p-12 text-center">
              <div className="w-20 h-20 bg-[#C17855] bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">⚠️</span>
              </div>
              
              <h1 className="text-3xl font-bold text-[#2C1810] mb-4">
                Something Went Wrong
              </h1>
              
              <p className="text-[#5A5046] mb-8">
                We couldn't verify your payment. Please contact support if you were charged.
              </p>
              
              <Link to="/">
                <Button className="bg-[#6B7A5C] hover:bg-[#5A6850] text-white px-8 py-3 rounded-lg">
                  Back to Home
                </Button>
              </Link>
            </Card>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;